import React, { useState } from 'react';
import { Pencil } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  updateClaimingProfiles,
  addClaimingProfiles,
  uploadClaimingProfileImage,
  deleteClaimingProfileImage,
} from '../services/claimingProfiles';

const ClaimProfileDetails = ({ profile, onClose }) => {
  const [formData, setFormData] = useState({
    _id: profile._id || '',
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    licenseType: profile.licenseType || '',
    agencyAffiliation: profile.agencyAffiliation || '',
    branchName: profile.branchName || '',
    ratingAverage: profile.ratingAverage || 0,
    ratingCount: profile.ratingCount || 0,
    primaryLocation: profile.primaryLocation || '',
    otherLocations: profile.otherLocations || [],
    otherLocationsInput: (profile.otherLocations || []).join(', '),
    overview: profile.overview || '',
    services: profile.services || [],
    servicesInput: (profile.services || []).join(', '),
    licenseNumber: profile.licenseNumber || '',
    licenseIssued: profile.licenseIssued || '',
    licenseExpires: profile.licenseExpires || '',
    claimURL: profile.claimURL || '',
    email: profile.email || '',
    mobile: profile.mobile ? profile.mobile.replace(/^\+1/, '') : '',
    type: profile.type || '',
    ratings: profile.ratings || [],
    premium: profile.premium || '',
  });

  const [activeTab, setActiveTab] = useState('overview');
  const [newImage, setNewImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, value) => {
    handleChange(
      field,
      value.split(',').map((v) => v.trim())
    );
  };

  const handleSubmit = async () => {
    // Email validation - required for new profiles
    if (!profile._id) {
      if (!formData.email || formData.email.trim() === '') {
        alert('Email is required.');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        alert('Please enter a valid email address.');
        return;
      }
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Validate License Issued - must be today or past date
    if (formData.licenseIssued) {
      const issuedDate = new Date(formData.licenseIssued);
      issuedDate.setHours(0, 0, 0, 0);
      if (issuedDate > today) {
        alert('License Issued date cannot be a future date');
        return;
      }
    }

    // Validate License Expires - must be today or future date
    if (formData.licenseExpires) {
      const expiryDate = new Date(formData.licenseExpires);
      expiryDate.setHours(0, 0, 0, 0);
      if (expiryDate < today) {
        alert('License Expires date cannot be a past date');
        return;
      }
    }

    // Validate for duplicate locations
    const primaryLoc = formData.primaryLocation.trim().toLowerCase();
    const otherLocs = formData.otherLocations.map(loc => loc.trim().toLowerCase());
    if (primaryLoc && otherLocs.includes(primaryLoc)) {
      alert('Primary location cannot be the same as other locations');
      return;
    }
    const uniqueOtherLocs = new Set(otherLocs);
    if (uniqueOtherLocs.size !== otherLocs.length) {
      alert('Duplicate locations are not allowed');
      return;
    }

    // Mobile Validation - Format mobile with +1 prefix 
    const formattedMobile = formData.mobile
      ? `+1${formData.mobile.replace(/^\+1/, '')}`
      : '';

    if (profile._id) {
      // TODO: Update API logic here
      const updatedData = {
        ...formData,
        mobile: formattedMobile,
      };
      const response = await updateClaimingProfiles(updatedData);
      if (response.error) {
        alert(response.error || 'Something went wrong');
      }
      if (response.message) {
        alert(response.message || 'Successfully updated');
        onClose();
      }
    } else {
      // TODO: Add new API logic here
      const newData = {
        ...formData,
        mobile: formattedMobile,
      };
      delete newData._id; // Ensure no _id is sent for new profiles
      const response = await addClaimingProfiles(newData);
      if (response.error) {
        alert(response.error || 'Something went wrong');
      }
      if (response.message) {
        alert(response.message || 'Successfully added');
        onClose();
      }
    }
  };

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'locations', label: 'Locations' },
    { key: 'license', label: 'License' },
    { key: 'services', label: 'Services' },
  ];

  const handleImageUpload = async () => {
    if (!newImage || !newImage.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', newImage);
    formData.append('profileId', profile._id);
    formData.append('oldImageUrl', profile.profileImageURL || '');

    const result = await uploadClaimingProfileImage(formData);
    setUploadingImage(false);

    if (result.error) alert(result.error);
    else alert('Image uploaded successfully!');
  };

  const handleImageDelete = async () => {
    const result = await deleteClaimingProfileImage(formData._id);
    if (result.error) alert(result.error);
    else alert('Image deleted successfully!');
  };

  const name =
    `${formData.firstName} ${formData.lastName}`.trim() || 'Unknown Name';

  return (
    <div className="relative max-w-7xl mx-auto bg-white p-4 mt-3 sm:p-6 shadow rounded-lg">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-500 hover:text-red-600 text-3xl font-bold"
      >
        &times;
      </button>

      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5 mb-4">
        <div className="flex flex-col items-center">
          <div className="relative group">
            <img
              src={profile.profileImageURL || '/profile.png'}
              alt={name}
              className="
                w-24
                h-24
                sm:w-28
                sm:h-28
                rounded-full
                object-cover
                border
                border-gray-300
                cursor-pointer
              "
              onClick={() => document.getElementById('profileImageInput').click()}
            />

            {/* Pencil Icon Overlay */}
            <button
              type="button"
              onClick={() => document.getElementById('profileImageInput').click()}
              className="
                absolute
                bottom-1
                right-1
                bg-black
                text-white
                p-2
                rounded-full
                shadow
                opacity-90
                hover:opacity-100
                transition
              "
              aria-label="Edit profile image"
            >
              <Pencil size={14} />
            </button>

            {/* Hidden File Input */}
            <input
              id="profileImageInput"
              type="file"
              accept="image/*"
              onChange={(e) => setNewImage(e.target.files[0])}
              className="hidden"
            />
          </div>

          <div className="flex gap-2 mt-3 flex-wrap justify-center">
            <button
              onClick={handleImageUpload}
              disabled={!newImage || uploadingImage}
              className="
                bg-green-600
                text-white
                px-4
                py-1.5
                rounded-md
                text-sm
                hover:bg-green-700
                transition
                disabled:opacity-50
              "
            >
              {uploadingImage ? 'Uploading...' : 'Upload'}
            </button>
            <button
              onClick={handleImageDelete}
              className="
                bg-red-500
                text-white
                px-4
                py-1.5
                rounded-md
                text-sm
                hover:bg-red-600
                transition
              "
            >
              Delete
            </button>
          </div>
        </div>

        <div className="flex-1 text-center sm:text-left space-y-3">
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              className="font-bold text-xl sm:text-2xl border border-gray-300 rounded px-3 py-2 w-full sm:w-auto"
              placeholder="First Name"
              required
            />
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              className="font-bold text-xl sm:text-2xl border border-gray-300 rounded px-3 py-2 w-full sm:w-auto"
              placeholder="Last Name"
            />
          </div>

          <p className="text-sm sm:text-base">
            <input
              value={formData.licenseType}
              onChange={(e) => handleChange('licenseType', e.target.value)}
              className="text-gray-600 border border-gray-300 rounded px-3 py-2 w-full sm:w-1/2"
              placeholder="License Type"
              required
            />
          </p>

          <p className="text-sm sm:text-base flex flex-wrap gap-2">
            <input
              value={formData.agencyAffiliation}
              onChange={(e) =>
                handleChange('agencyAffiliation', e.target.value)
              }
              className="text-gray-700 border border-gray-300 rounded px-3 py-2 w-full sm:w-auto"
              placeholder="Agency Affiliation"
              required
            />
            {/* <span className="hidden sm:inline">—</span> */}
            <input
              value={formData.branchName}
              onChange={(e) => handleChange('branchName', e.target.value)}
              className="text-gray-700 border border-gray-300 rounded px-3 py-2 w-full sm:w-auto"
              placeholder="Branch Name"
              required
            />
          </p>

          <p className="text-sm sm:text-base text-gray-500 space-y-2">
            📧{' '}
            <input
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full sm:w-3/4 mt-1"
              placeholder="Email"
              required
            />
            <br />
            📞{' '}
            <input
              value={formData.mobile}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                const sanitized = val.length > 10 ? val.slice(-10) : val;
                handleChange('mobile', sanitized);
              }}
              className="border border-gray-300 rounded px-3 py-2 w-full sm:w-3/4 mt-1"
              placeholder="XXXXXXXXXX"
              required
            />
            {/* <PhoneInput
              country={'in'}
              value={formData.mobile}
              onChange={(phone) => handleChange('mobile', phone)}
              inputProps={{
                name: 'mobile',
                required: true,
                placeholder: 'Enter mobile number',
              }}
            /> */}
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 mb-6 text-gray-700 text-sm sm:text-base">
        <span className="text-2xl mt-1">📍</span>

        <div className="flex flex-col w-full sm:w-1/2">
          <p className="font-semibold text-base sm:text-lg mb-1">
            {formData.branchName}
          </p>

          <input
            value={formData.primaryLocation}
            onChange={(e) => handleChange('primaryLocation', e.target.value)}
            placeholder="Primary Location"
            className="mb-2 px-3 py-2 border border-gray-300 rounded "
            required
          />

          <label className="block text-xs text-gray-500 mb-1">
            Other Locations ({formData.otherLocations.length}/4 max)
          </label>
          <input
            value={formData.otherLocationsInput}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                otherLocationsInput: e.target.value,
              }))
            }
            onBlur={() => {
              const locations = formData.otherLocationsInput
                .split(',')
                .map((loc) => loc.trim())
                .filter(Boolean);

              if (locations.length > 4) {
                alert('Maximum 4 other locations allowed. Only the first 4 will be saved.');
                const limitedLocations = locations.slice(0, 4);
                setFormData((prev) => ({
                  ...prev,
                  otherLocations: limitedLocations,
                  otherLocationsInput: limitedLocations.join(', '),
                }));
                return;
              }

              // Check for duplicates
              const primaryLoc = formData.primaryLocation.trim().toLowerCase();
              const lowerLocations = locations.map(loc => loc.trim().toLowerCase());

              // Check if any location matches primary
              if (primaryLoc && lowerLocations.includes(primaryLoc)) {
                alert('Other locations cannot be the same as primary location');
                return;
              }

              // Check for duplicates within other locations
              const uniqueLocs = new Set(lowerLocations);
              if (uniqueLocs.size !== lowerLocations.length) {
                alert('Duplicate locations are not allowed');
                return;
              }

              setFormData((prev) => ({
                ...prev,
                otherLocations: locations,
                otherLocationsInput: locations.join(', '),
              }));
            }}
            placeholder="Other Locations (comma-separated, max 4)"
            className="px-3 py-2 border border-gray-300 rounded "
            required
          />
        </div>
      </div>

      {/* premium update*/}
      <p className="text-sm sm:text-base mb-3">
        <label className="mr-2 font-semibold">Premium:</label>
        <select
          value={
            formData.premium === true
              ? 'true'
              : formData.premium === false
                ? 'false'
                : ''
          }
          // onChange={(e) => {
          //   const updated = [...formData.premium];

          //   setFormData((prev) => ({ ...prev, premium: updated }));
          // }}
          onChange={(e) => handleChange('premium', e.target.value === 'true')}
          className="border rounded p-1 text-sm"
        >
          <option value="">Select</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </p>

      {/* ratings */}
      {formData.ratings && formData.ratings.length > 0 && (
        <div className="mt-6">
          <h3 className="text-base sm:text-lg font-semibold mb-2">Ratings</h3>
          <div className="space-y-3">
            {formData.ratings.map((rating, idx) => (
              <div key={idx} className="p-3 border rounded bg-gray-50">
                <div className="mb-2">
                  <span className="font-medium">User:</span>{' '}
                  {rating.userName || 'Anonymous'}
                </div>
                <div className="flex items-center gap-2">
                  <label className="font-medium">Score:</label>
                  <input
                    type="number"
                    min={1}
                    max={5}
                    value={rating.score}
                    onChange={(e) => {
                      const updated = [...formData.ratings];
                      updated[idx].score = parseInt(e.target.value);
                      setFormData((prev) => ({ ...prev, ratings: updated }));
                    }}
                    className="border p-1 w-16"
                  />
                </div>
                <textarea
                  className="w-full mt-2 border p-2 rounded"
                  value={rating.comment}
                  onChange={(e) => {
                    const updated = [...formData.ratings];
                    updated[idx].comment = e.target.value;
                    setFormData((prev) => ({ ...prev, ratings: updated }));
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-b flex flex-wrap gap-3 sm:gap-4 mb-4 justify-center sm:justify-start text-sm sm:text-base">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`pb-1 sm:pb-2 font-medium ${activeTab === tab.key
              ? 'text-black border-b-2 border-blue-600'
              : 'text-gray'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="text-gray-800 text-sm sm:text-base">
        {activeTab === 'overview' && (
          <div>
            <h3 className="text-base sm:text-lg font-semibold mb-2">
              Overview
            </h3>
            <textarea
              value={formData.overview}
              onChange={(e) => handleChange('overview', e.target.value)}
              rows={4}
              className="w-full border p-2 rounded"
            />
          </div>
        )}

        {activeTab === 'locations' && (
          <div>
            <h3 className="text-base sm:text-lg font-semibold mb-2">
              Locations
            </h3>
            {(formData.primaryLocation ||
              formData.otherLocations?.length > 0) && (
                <ul className="list-disc pl-5">
                  {formData.primaryLocation && (
                    <li>{formData.primaryLocation}</li>
                  )}
                  {formData.otherLocations?.map((loc, i) => (
                    <li key={i}>{loc}</li>
                  ))}
                </ul>
              )}
          </div>
        )}

        {activeTab === 'license' && (
          <div>
            <h3 className="text-base sm:text-lg font-semibold mb-2">
              License Info
            </h3>
            <p>
              <strong>License Number:</strong>{' '}
              <input
                value={formData.licenseNumber}
                onChange={(e) => handleChange('licenseNumber', e.target.value)}
                required
              />
            </p>
            <p>
              <strong>Issued:</strong>{' '}
              <input
                type="date"
                value={formData.licenseIssued}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => handleChange('licenseIssued', e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 ml-2"
                required
              />
              <span className="text-xs text-gray-500 ml-2">(Must be today or past)</span>
            </p>
            <p>
              <strong>Expires:</strong>{' '}
              <input
                type="date"
                value={formData.licenseExpires}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => handleChange('licenseExpires', e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 ml-2"
                required
              />
              <span className="text-xs text-gray-500 ml-2">(Must be today or future)</span>
            </p>
          </div>
        )}

        {activeTab === 'services' && (
          <div>
            <h3 className="text-base sm:text-lg font-semibold mb-2">
              Services Offered
            </h3>
            <textarea
              value={formData.servicesInput}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  servicesInput: e.target.value,
                }))
              }
              onBlur={() => {
                const cleaned = formData.servicesInput
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean);

                setFormData((prev) => ({
                  ...prev,
                  services: cleaned,
                  servicesInput: cleaned.join(', '),
                }));
              }}
              className="w-full border p-2 rounded"
              rows={3}
              placeholder="Enter services separated by commas"
            />
          </div>
        )}
      </div>

      <div className="text-right mt-6">
        <button
          onClick={handleSubmit}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow"
        >
          {profile._id ? 'Update' : 'Add New'}
        </button>
      </div>
    </div>
  );
};

export default ClaimProfileDetails;
